import { Skeleton } from "@mui/material";
import { useIsDesktop } from "@repo/ui";
import { navigate, type PageProps } from "gatsby";

import { ProductType } from "@repo/api";
import { assertIsLifeContractType } from "@repo/logic";
import { ContractIDProvider, extractContractIDFromPageProps } from "../../context/ContractIDContext";
import { ContractTypeProvider } from "../../context/ContractTypeContext";
import { useContractById } from "../../queryHooks";
import { BackNavigationBar, LoggedInBar } from "../AppBar/Bars";
import { BottomNavigationDesktopComponent } from "../BottomNavigation";
import { FufiSubtitle, LifeSubtitle } from "./Heading/Subtitle";
import { FufiTitle, LifeTitle } from "./Heading/Title";

type ICommonLifeAppBar = {
    contractId: string;
    proposalNumber?: string | null;
};

const LifeAppBar = ({ contractId, proposalNumber }: ICommonLifeAppBar) => {
    return (
        <ContractIDProvider contractId={contractId} proposalNumber={proposalNumber}>
            <ContractTypeProvider contractType={ProductType.Cl}>
                <BackNavigationBar
                    backHandler={() => {
                        navigate("/smlouvy/");
                    }}
                    title={<LifeTitle />}
                    subtitle={<LifeSubtitle />}
                />
            </ContractTypeProvider>
        </ContractIDProvider>
    );
};

const FufiAppBar = ({ contractId, proposalNumber }: ICommonLifeAppBar) => {
    return (
        <ContractIDProvider contractId={contractId} proposalNumber={proposalNumber}>
            <ContractTypeProvider contractType={ProductType.Clf}>
                <BackNavigationBar
                    backHandler={() => {
                        navigate("/smlouvy/");
                    }}
                    title={<FufiTitle />}
                    subtitle={<FufiSubtitle />}
                />
            </ContractTypeProvider>
        </ContractIDProvider>
    );
};

const LoadingBar = () => {
    return (
        <BackNavigationBar
            backHandler={() => {
                navigate("/smlouvy/");
            }}
            title={<Skeleton />}
            subtitle={<Skeleton />}
        />
    );
};

const AppBarByContractType = {
    [ProductType.Clf]: FufiAppBar,
    [ProductType.Cl]: LifeAppBar,
};

export const CommonLifeAppBar = (pageProps: PageProps) => {
    const contractId = extractContractIDFromPageProps(pageProps);
    const isDesktop = useIsDesktop();

    const { data, isSuccess } = useContractById({
        id: contractId,
        options: { enabled: !isDesktop },
    });

    if (isDesktop) return <LoggedInBar BottomNavigationComponent={BottomNavigationDesktopComponent} />;

    if (!isSuccess) return <LoadingBar />;

    assertIsLifeContractType(data.type);

    const AppBar = AppBarByContractType[data.type];

    return <AppBar contractId={contractId} proposalNumber={data.proposalNumber} />;
};
